import React from 'react';
import clsx from 'clsx';
import styles from './Card.module.css';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'primary' | 'secondary';
  children: React.ReactNode;
}

// Card component with sub-components interface
interface CardComponent extends React.FC<CardProps> {
  Header: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Content: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Footer: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Title: React.FC<React.HTMLAttributes<HTMLHeadingElement>>;
  QuickActionsGrid: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  QuickActionButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  QuickActionText: React.FC<React.HTMLAttributes<HTMLDivElement>>;
}

const Card: React.FC<CardProps> = ({ variant = 'default', className, children, ...props }) => {
  return (
    <div
      className={clsx(
        styles.card,
        variant === 'primary' && styles.primary,
        variant === 'secondary' && styles.secondary,
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Header Sub-component
const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.header, className)} {...props}>
      {children}
    </div>
  );
};

// Card Content Sub-component
const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.content, className)} {...props}>
      {children}
    </div>
  );
};

// Card Footer Sub-component
const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.footer, className)} {...props}>
      {children}
    </div>
  );
};

// Card Title Sub-component
const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className, children, ...props }) => {
  return (
    <h3 className={clsx(styles.title, className)} {...props}>
      {children}
    </h3>
  );
};

// Quick Actions Grid Sub-component
const QuickActionsGrid: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.quickActionsGrid, className)} {...props}>
      {children}
    </div>
  );
};

// Quick Action Button Sub-component
const QuickActionButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <button className={clsx(styles.quickActionButton, className)} {...props}>
      {children}
    </button>
  );
};

// Quick Action Text Sub-component
const QuickActionText: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, children, ...props }) => {
  return (
    <div className={clsx(styles.quickActionText, className)} {...props}>
      {children}
    </div>
  );
};

// Attach sub-components to Card
(Card as CardComponent).Header = CardHeader;
(Card as CardComponent).Content = CardContent;
(Card as CardComponent).Footer = CardFooter;
(Card as CardComponent).Title = CardTitle;
(Card as CardComponent).QuickActionsGrid = QuickActionsGrid;
(Card as CardComponent).QuickActionButton = QuickActionButton;
(Card as CardComponent).QuickActionText = QuickActionText;

export default Card as CardComponent;
